/**
 * InSessionToolbar (Spec D) — overlay component that drops onto any therapist
 * video-call view. Provides four primary actions:
 *
 *   1. Mark moment    — quick-tag the current point in the session
 *   2. Quick note     — jot a free-form note
 *   3. Trigger assessment — push PHQ-9 / GAD-7 / etc. to the client mid-call
 *   4. AI Supervisor  — collapsible side panel for clinical reasoning support
 *
 * Plus an end-of-session checklist banner that auto-appears 5 minutes before
 * scheduledEnd (when known).
 *
 * V1: not auto-mounted on the call page yet — call team mounts it where it
 * belongs. Standalone + functional. Pass { liveCallId, scheduledEnd? }.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Stack, Button, IconButton, Tooltip, Drawer, TextField, Typography,
  Card, CardContent, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel, Chip
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { rawApiClient as apiClient } from '../../services/api';

const MOMENT_LABELS = [
  { value: 'CLINICALLY_SIGNIFICANT', label: 'Clinically significant', color: 'primary' },
  { value: 'FOLLOW_UP', label: 'Follow up', color: 'secondary' },
  { value: 'RISK', label: 'Risk', color: 'error' },
  { value: 'INSIGHT', label: 'Insight', color: 'success' },
  { value: 'OTHER', label: 'Other', color: 'default' }
];

// Assessment options resolved from the 3-tier governance
// (service-group library → defaults → therapist grants).
// Falls back gracefully to a stable sentinel option if the resolver returns nothing.
const FALLBACK_OPTIONS = [
  { value: 'phq9', label: 'PHQ-9 (depression)' },
  { value: 'gad7', label: 'GAD-7 (anxiety)' }
];

export default function InSessionToolbar({ liveCallId, scheduledEnd, onError }) {
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  // Mark moment
  const [momentLabel, setMomentLabel] = useState(null);
  const [momentNote, setMomentNote] = useState('');

  // Quick note
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickNoteText, setQuickNoteText] = useState('');

  // Assessment
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState('phq9');
  const [assessmentOptions, setAssessmentOptions] = useState(FALLBACK_OPTIONS);

  // Resolve the therapist's available assessments once on mount. Resolver
  // intersects service-group library + defaults with per-therapist grants.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/assessment-governance/therapist/available');
        if (cancelled) return;
        const defs = res?.data?.testDefinitions || [];
        if (defs.length > 0) {
          setAssessmentOptions(defs.map(d => ({
            value: d.testKey,
            label: d.shortName ? `${d.shortName} — ${d.fullName || ''}`.trim() : (d.fullName || d.testKey)
          })));
          if (!defs.some(d => d.testKey === 'phq9')) {
            setSelectedAssessment(defs[0].testKey);
          }
        }
      } catch (err) {
        // Non-fatal — keep fallback options
        console.warn('[InSessionToolbar] assessment resolver failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // AI Supervisor
  const [supervisorPanelOpen, setSupervisorPanelOpen] = useState(false);
  const [supervisorQuestion, setSupervisorQuestion] = useState('');
  const [supervisorThread, setSupervisorThread] = useState([]); // [{q, a, ts}]
  const [supervisorThinking, setSupervisorThinking] = useState(false);

  // End-of-session banner
  const [endBannerDismissed, setEndBannerDismissed] = useState(false);
  const minutesToEnd = scheduledEnd
    ? Math.round((new Date(scheduledEnd).getTime() - Date.now()) / 60000)
    : null;
  const showEndBanner = !endBannerDismissed
    && minutesToEnd !== null
    && minutesToEnd >= 0
    && minutesToEnd <= 5;

  const apiBase = `/calls/${liveCallId}/in-session`;

  const handleErr = (err) => {
    const msg = err?.response?.data?.error || err?.message || 'Action failed';
    setError(msg);
    if (onError) onError(msg);
  };

  async function postMoment(label) {
    try {
      await apiClient.post(`${apiBase}/mark-moment`, {
        label,
        note: label === momentLabel ? momentNote : '',
        sessionOffsetMs: -1
      });
      setToast(`Moment marked: ${MOMENT_LABELS.find(l => l.value === label)?.label || label}`);
      setMomentLabel(null);
      setMomentNote('');
    } catch (err) {
      handleErr(err);
    }
  }

  async function submitQuickNote() {
    if (!quickNoteText.trim()) return;
    try {
      await apiClient.post(`${apiBase}/quick-note`, { text: quickNoteText, sessionOffsetMs: -1 });
      setToast('Quick note saved');
      setQuickNoteText('');
      setQuickNoteOpen(false);
    } catch (err) {
      handleErr(err);
    }
  }

  async function triggerAssessment() {
    try {
      await apiClient.post(`${apiBase}/trigger-assessment`, { assessmentKey: selectedAssessment });
      setToast(`Assessment sent to client: ${assessmentOptions.find(o => o.value === selectedAssessment)?.label || selectedAssessment}`);
      setAssessmentDialogOpen(false);
    } catch (err) {
      handleErr(err);
    }
  }

  async function askSupervisor() {
    if (!supervisorQuestion.trim()) return;
    const q = supervisorQuestion;
    setSupervisorQuestion('');
    setSupervisorThinking(true);
    try {
      const res = await apiClient.post(`${apiBase}/ai-supervisor-consult`, { question: q });
      setSupervisorThread((prev) => [
        ...prev,
        { q, a: res.data?.answer || '(no response)', ts: new Date() }
      ]);
    } catch (err) {
      handleErr(err);
      setSupervisorThread((prev) => [...prev, { q, a: '(failed to consult)', ts: new Date() }]);
    } finally {
      setSupervisorThinking(false);
    }
  }

  return (
    <>
      {/* The toolbar itself — fixed bottom-right, low z-index so it never blocks
          the video. Designed to drop into any call view. */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1200,
          backdropFilter: 'blur(8px)',
          bgcolor: 'rgba(255,255,255,0.92)',
          borderRadius: 2,
          boxShadow: 4,
          p: 1
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Mark moment — opens a small popover with label chips */}
          <Box sx={{ position: 'relative' }}>
            <Tooltip title="Mark moment" placement="top">
              <IconButton color="primary" onClick={() => setMomentLabel(momentLabel ? null : 'CLINICALLY_SIGNIFICANT')}>
                <BookmarkIcon />
              </IconButton>
            </Tooltip>
            {momentLabel && (
              <Card
                variant="outlined"
                sx={{
                  position: 'absolute',
                  bottom: '110%',
                  right: 0,
                  width: 320,
                  p: 1.5,
                  zIndex: 1300
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Mark this moment</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {MOMENT_LABELS.map(opt => (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        color={opt.color}
                        variant={momentLabel === opt.value ? 'filled' : 'outlined'}
                        onClick={() => setMomentLabel(opt.value)}
                        size="small"
                      />
                    ))}
                  </Stack>
                  <TextField
                    placeholder="Optional note…"
                    multiline
                    minRows={2}
                    size="small"
                    fullWidth
                    value={momentNote}
                    onChange={(e) => setMomentNote(e.target.value)}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => { setMomentLabel(null); setMomentNote(''); }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => postMoment(momentLabel)}>
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            )}
          </Box>

          <Tooltip title="Quick note" placement="top">
            <IconButton onClick={() => setQuickNoteOpen(true)}>
              <EditNoteIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Trigger assessment" placement="top">
            <IconButton onClick={() => setAssessmentDialogOpen(true)}>
              <AssignmentTurnedInIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="AI Supervisor" placement="top">
            <IconButton onClick={() => setSupervisorPanelOpen(true)}>
              <PsychologyIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* End-of-session banner — auto-appears 5 minutes before scheduledEnd */}
      {showEndBanner && (
        <Alert
          severity="info"
          sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1300, maxWidth: 600 }}
          action={<IconButton size="small" onClick={() => setEndBannerDismissed(true)}><CloseIcon fontSize="small" /></IconButton>}
        >
          <strong>Session ends in {minutesToEnd} {minutesToEnd === 1 ? 'minute' : 'minutes'}.</strong> Time to: summarise key takeaways · review safety plan if relevant · agree next-step homework · confirm next session date.
        </Alert>
      )}

      {/* Quick note dialog */}
      <Dialog open={quickNoteOpen} onClose={() => setQuickNoteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Quick note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            placeholder="A quick note for the post-session draft…"
            value={quickNoteText}
            onChange={(e) => setQuickNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickNoteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitQuickNote}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Assessment trigger dialog */}
      <Dialog open={assessmentDialogOpen} onClose={() => setAssessmentDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Send assessment to client</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Assessment</InputLabel>
            <Select value={selectedAssessment} label="Assessment" onChange={(e) => setSelectedAssessment(e.target.value)}>
              {assessmentOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            The client will see this in their portal. Results return to your pane in seconds.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssessmentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={triggerAssessment}>Send</Button>
        </DialogActions>
      </Dialog>

      {/* AI Supervisor side panel */}
      <Drawer
        anchor="right"
        open={supervisorPanelOpen}
        onClose={() => setSupervisorPanelOpen(false)}
        PaperProps={{ sx: { width: { xs: '90vw', sm: 420 } } }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">AI Supervisor</Typography>
            <IconButton onClick={() => setSupervisorPanelOpen(false)} size="small"><CloseIcon /></IconButton>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            Therapist-only. Does not diagnose, direct care, or replace human supervision.
          </Typography>
          <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
            <Stack spacing={2}>
              {supervisorThread.length === 0 && !supervisorThinking && (
                <Typography variant="body2" color="text.secondary">
                  Ask anything — formulation prompts, modality fit, risk-assessment framing.
                </Typography>
              )}
              {supervisorThread.map((m, idx) => (
                <Card key={idx} variant="outlined">
                  <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary">You</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>{m.q}</Typography>
                    <Typography variant="caption" color="text.secondary">AI Supervisor</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.a}</Typography>
                  </CardContent>
                </Card>
              ))}
              {supervisorThinking && (
                <Typography variant="body2" color="text.secondary">Thinking…</Typography>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              value={supervisorQuestion}
              onChange={(e) => setSupervisorQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askSupervisor(); } }}
              multiline
              maxRows={3}
              fullWidth
              size="small"
              placeholder="Ask the AI Supervisor…"
              disabled={supervisorThinking}
            />
            <Button
              variant="contained"
              onClick={askSupervisor}
              disabled={supervisorThinking || !supervisorQuestion.trim()}
              endIcon={<SendIcon />}
            >
              Ask
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </>
  );
}
